import { CommonModule } from "@angular/common";
import { afterNextRender, Component, inject, signal } from "@angular/core";
import { ActivatedRoute, Router, RouterOutlet } from "@angular/router";
import { CLIENT_ACCESS_TOKEN_KEY } from "@malirang/common";
import { LocalStorageService, Spinner, ToastService } from "@malirang/front-share";
import { injectNavigationEnd } from "ngxtension/navigation-end";
import { CenterUserStore } from "../stores/center/center-user.store";
import { CenterStore } from "../stores/center/center.store";
import { CenterRoleStore } from "../stores/center/role.store";
import { UserStore } from "../stores/user.store";
import { AuthGuardComponent } from "./auth-guard/auth-guard.component";
import { CenterGuardComponent } from "./center-guard/center-guard.component";
import { GuardStore } from "./guard.store";
import { NoRolePage } from "./role-guard/no-role.page";
import { NoRoleStore } from "./role-guard/no-role.store";

@Component({
    selector: "app-guard",
    templateUrl: "./guard.component.html",
    imports: [CommonModule, RouterOutlet, CenterGuardComponent, AuthGuardComponent, NoRolePage, Spinner],
})
export class GuardComponent {
    readonly centerStore = inject(CenterStore);
    readonly centerRoleStore = inject(CenterRoleStore);
    readonly centerUserStore = inject(CenterUserStore);
    readonly userStore = inject(UserStore);
    readonly guardStore = inject(GuardStore);

    readonly route = inject(ActivatedRoute);
    readonly router = inject(Router);
    readonly localStorageService = inject(LocalStorageService);
    readonly toastService = inject(ToastService);
    readonly navigationEnd$ = injectNavigationEnd();
    readonly noRoleStore = inject(NoRoleStore);

    render = signal(false);
    noRole = this.noRoleStore.noRole;

    constructor() {
        afterNextRender(() => {
            // 인증 처리
            this.handleAuthenticating();

            // path 설정
            this.guardStore.setPath(this.router.url);
            this.navigationEnd$.subscribe({
                next: (event) => {
                    this.guardStore.setPath(event.url);
                    this.handleRequiredRoles(this.route);
                },
            });

            // 센터 정보가 변경될 때마다 현재 페이지의 권한을 체크합니다.
            this.centerRoleStore.myRole$.subscribe({
                next: () => {
                    this.handleRequiredRoles(this.route);
                },
            });
        });
    }

    /**
     * 인증 처리
     */
    async handleAuthenticating() {
        const accessToken = await this.localStorageService.get(CLIENT_ACCESS_TOKEN_KEY);
        let user = null;

        if (accessToken) {
            try {
                user = await this.userStore.fetchUser();
                this.userStore.setUser(user);
                this.render.set(true);
            } catch {
                this.userStore.clearUser();
                this.render.set(true);
            }

            if (user) {
                // 센터 정보가 있는 경우 센터 정보를 가져옴
                if (user.centerCount) {
                    await this.centerStore.fetchCenters();
                    await this.centerStore.fetchCount();
                    await this.centerUserStore.fetchCenterUser();
                } else {
                    // 로그인 후 첫 진입 시 가이드 모달 오픈
                    const accessed = await this.localStorageService.get("firstAccess");
                    if (!accessed) {
                        this.guardStore.openGuideModal();
                        await this.localStorageService.set("firstAccess", true);
                    }
                }
            }
        }
        this.render.set(true);
        this.handleRequiredRoles(this.route);
    }

    /**
     * route에 설정된 requiredRoles를 확인하여 권한이 없는 경우 no-role 페이지로 이동합니다.
     * (children이 존재할 경우 재귀적으로 호출합니다.)
     * @param route
     */
    handleRequiredRoles(route: ActivatedRoute) {
        const roles = route.snapshot.data["requiredRoles"] || [];

        if (roles.length) {
            const myRole = this.centerRoleStore.myRole();

            if (myRole) {
                roles.map((role: string) => {
                    this.noRoleStore.setNoRole(!myRole.permissions.includes(role));
                });
            }
        } else {
            this.noRoleStore.setNoRole(false);
        }

        if (route.children.length > 0) {
            route.children.map((child) => {
                this.handleRequiredRoles(child);
            });
        }
    }
}
